import React, { useEffect, useState, useCallback } from 'react';
import api from '@/lib/api';
import { PageHeader } from '@/components/app/PageHeader';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { Plus, Pencil, Trash2 } from 'lucide-react';
import { toast } from 'sonner';

const EMPTY = { name: '', firm_name: '', mobile: '', email: '' };

export default function AdminConsultants() {
  const [items, setItems] = useState([]);
  const [edit, setEdit] = useState(null);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [confirmDelete, setConfirmDelete] = useState(null);

  const load = useCallback(() => api.get('/consultants').then((r) => setItems(r.data)).catch(() => toast.error('Failed to load consultants')), []);
  useEffect(() => { load(); }, [load]);

  const startCreate = () => { setForm(EMPTY); setCreating(true); };
  const startEdit = (c) => { setEdit(c); setForm({ name: c.name || '', firm_name: c.firm_name || '', mobile: c.mobile || '', email: c.email || '' }); };

  const save = async () => {
    if (!form.name.trim()) return toast.error('Name is required');
    try {
      if (edit) {
        await api.put(`/consultants/${edit.id}`, form);
        toast.success('Consultant updated');
      } else {
        await api.post('/consultants', form);
        toast.success('Consultant added');
      }
      setEdit(null); setCreating(false); setForm(EMPTY); load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
  };

  const remove = async () => {
    try {
      await api.delete(`/consultants/${confirmDelete.id}`);
      toast.success('Consultant deleted');
      setConfirmDelete(null); load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Cannot delete (in use)'); }
  };

  const isOpen = creating || !!edit;
  const closeForm = (v) => { if (!v) { setCreating(false); setEdit(null); setForm(EMPTY); } };

  return (
    <div>
      <PageHeader title="Consultants" subtitle="Property consultants managing properties" actions={
        <Button onClick={startCreate} data-testid="admin-consultants-add"><Plus className="h-4 w-4 mr-1" /> Add consultant</Button>
      } />

      <Card className="p-0 overflow-x-auto">
        <table className="w-full min-w-[720px] text-sm">
          <thead className="bg-muted/60">
            <tr>
              <th className="text-left px-3 py-2 text-xs uppercase text-muted-foreground">Name</th>
              <th className="text-left px-3 py-2 text-xs uppercase text-muted-foreground">Firm</th>
              <th className="text-left px-3 py-2 text-xs uppercase text-muted-foreground">Mobile</th>
              <th className="text-left px-3 py-2 text-xs uppercase text-muted-foreground">Email</th>
              <th className="px-3 py-2 text-xs uppercase text-muted-foreground text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {items.length === 0 && (
              <tr><td colSpan={5} className="px-3 py-8 text-center text-muted-foreground">No consultants yet.</td></tr>
            )}
            {items.map((c) => (
              <tr key={c.id} className="border-t hover:bg-muted/40" data-testid={`consultant-row-${c.id}`}>
                <td className="px-3 py-2 font-medium">{c.name}</td>
                <td className="px-3 py-2">{c.firm_name || '—'}</td>
                <td className="px-3 py-2">{c.mobile || '—'}</td>
                <td className="px-3 py-2">{c.email || '—'}</td>
                <td className="px-3 py-2 text-right whitespace-nowrap">
                  <Button size="sm" variant="ghost" className="h-8 px-2" onClick={() => startEdit(c)} data-testid={`consultant-edit-${c.id}`} aria-label="Edit consultant">
                    <Pencil className="h-3.5 w-3.5" />
                  </Button>
                  <Button size="sm" variant="ghost" className="h-8 px-2 text-destructive hover:text-destructive" onClick={() => setConfirmDelete(c)} data-testid={`consultant-delete-${c.id}`} aria-label="Delete consultant">
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>

      <Dialog open={isOpen} onOpenChange={closeForm}>
        <DialogContent className="sm:max-w-[520px] max-h-[92dvh] overflow-y-auto">
          <DialogHeader><DialogTitle>{edit ? 'Edit consultant' : 'Add consultant'}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="sm:col-span-2"><Label>Name <span className="text-destructive">*</span></Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="consultant-form-name" /></div>
            <div className="sm:col-span-2"><Label>Firm name</Label><Input value={form.firm_name} onChange={(e) => setForm({ ...form, firm_name: e.target.value })} data-testid="consultant-form-firm" /></div>
            <div><Label>Mobile</Label><Input value={form.mobile} onChange={(e) => setForm({ ...form, mobile: e.target.value })} data-testid="consultant-form-mobile" /></div>
            <div><Label>Email</Label><Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="consultant-form-email" /></div>
          </div>
          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button variant="outline" onClick={() => closeForm(false)} className="w-full sm:w-auto">Cancel</Button>
            <Button onClick={save} className="w-full sm:w-auto" data-testid="consultant-form-save">{edit ? 'Save changes' : 'Add consultant'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!confirmDelete} onOpenChange={(v) => !v && setConfirmDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete consultant?</AlertDialogTitle>
            <AlertDialogDescription>
              This will remove <strong>{confirmDelete?.name}</strong>. Consultants assigned to active properties cannot be deleted.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={remove} className="bg-destructive text-destructive-foreground hover:bg-destructive/90" data-testid="consultant-delete-confirm">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
